(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    // AMD
    define(['underscore', 'jquery', 'torso/modules/View', 'templates'], factory);
  } else if (typeof exports === 'object') {
    // Node, CommonJS-like
    var templates = {
      screenSizeTemplate: require('./screenSizeTemplate.hbs')
    };
    module.exports = factory(require('underscore'), require('jquery'), require('backbone-torso/modules/View'), templates);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function (_, $, TorsoView, templates) {
  'use strict';

  var RESIZE_THROTTLE_MS_BETWEEN_CALLS = 100;

  /*;*
   * These screen sizes are duplicated in screenSizeTemplate.hbs.  Each one needs to map one-to-one with the possible [data-screen-size-value].
   * See SCREEN_SIZES property of screenSizeView for details on each size.
   * Ordinal is used for relative size comparisons.
   * @type {{SMALL: {name: String, ordinal: Number}, MEDIUM: {name: String, ordinal: Number}, MEDIUM_LANDSCAPE: {name: String, ordinal: Number}, LARGE: {name: String, ordinal: Number}, EXTRA_LARGE: {name: String, ordinal: Number}}}
   */
  var SCREEN_SIZES_CONFIGURATION = {
    SMALL: {
      name: 'SMALL',
      ordinal: 0
    },
    MEDIUM: {
      name: 'MEDIUM',
      ordinal: 1
    },
    MEDIUM_LANDSCAPE: {
      name: 'MEDIUM_LANDSCAPE',
      ordinal: 2
    },
    LARGE: {
      name: 'LARGE',
      ordinal: 3
    },
    EXTRA_LARGE: {
      name: 'EXTRA_LARGE',
      ordinal: 4
    }
  };

  /**
   * Finds the first configuration with the given screen name (undefined if none found).
   * Name is unique so the first one is the only one.
   * @param screenSizeName {String} the name of the screen size to get the config for.
   * @return {Object} Screen size configuration with the given name.
   */
  function getScreenConfigurationByName(screenSizeName) {
    return _.findWhere(SCREEN_SIZES_CONFIGURATION, { name: screenSizeName });
  }

  /**
   * Compares the size of 2 screen size configurations based on the ordinal values.
   * Screens names not in the list are smaller than SMALL.
   * @param screenSizeConfiguration1 {Object} screen size 1 configuration.
   * @param screenSizeConfiguration2 {Object} screen size 2 configuration.
   * @return {Number} a value < 0 if size1 is smaller than size2.  > 0 if size1 larger than size2 and == 0 if they are the same.
   */
  function compareScreenSizeConfigurations(screenSizeConfiguration1, screenSizeConfiguration2) {
    var ordinal1 = (screenSizeConfiguration1 && screenSizeConfiguration1.ordinal) || -1;
    var ordinal2 = (screenSizeConfiguration2 && screenSizeConfiguration2.ordinal) || -1;
    return ordinal1 - ordinal2;
  }

  /**
   * Compares the size of 2 screen size configurations based on the ordinal values.
   * Screens names not in the list are smaller than SMALL.
   * @param screenSizeName1 {String} screen size 1 name.
   * @param screenSizeName2 {String} screen size 2 name.
   * @return {Number} a value < 0 if size1 is smaller than size2.  > 0 if size1 larger than size2 and == 0 if they are the same.
   */
  function compareScreenSizeConfigurationsByName(screenSizeName1, screenSizeName2) {
    var screenSizeName1Configuration = getScreenConfigurationByName(screenSizeName1);
    var screenSizeName2Configuration = getScreenConfigurationByName(screenSizeName2);
    return compareScreenSizeConfigurations(screenSizeName1Configuration, screenSizeName2Configuration);
  }

  /**
   * Widget to detect the screen size and emit events when
   * the screen changes between css media breakpoints.
   *
   * _settings-responsive.scss is used to encode the breakpoints between
   * screen sizes in css.  Use deactivate() & activate() to pause listening.
   *
   * @class screenSizeView
   * @static
   */
  var ScreenSizeView = TorsoView.extend({
    tagName: 'span',
    template: templates.screenSizeTemplate,

    /**
     * @property SCREEN_SIZES {Object} the screen sizes as defined in _settings-responsive.scss
     *   @property SCREEN_SIZES.SMALL {String} 'SMALL' - Smallest screen size defined as $medium-screen-width > width
     *   @property SCREEN_SIZES.MEDIUM {String} 'MEDIUM' - Tablet Portrait screen size defined as $medium-screen-width > width > $medium-screen-landscape-width
     *   @property SCREEN_SIZES.MEDIUM_LANDSCAPE {String} 'MEDIUM LANDSCAPE' - Normal Desktop screen size defined as $medium-screen-landscape-width > width > $large-screen-width
     *   @property SCREEN_SIZES.LARGE {String} 'LARGE' - Normal Desktop screen size defined as $large-screen-width > width > $extra-large-screen-width
     *   @property SCREEN_SIZES.EXTRA_LARGE {String} 'EXTRA LARGE' - Largest screen size defined as $extra-large-screen-width > width
     */
    SCREEN_SIZES: _.mapObject(SCREEN_SIZES_CONFIGURATION, function(configuration) {
      return configuration.name;
    }),

    /**
     * @method compareToCurrentSize
     * @param screenSizeToCompare {String} one of SCREEN_SIZES
     * @return {Number} greater than 0 if the current screen size is larger, less than zero if smaller and zero if equal.
     */
    compareToCurrentSize: function(screenSizeToCompare) {
      return compareScreenSizeConfigurationsByName(this.getScreenSize(), screenSizeToCompare);
    },

    /**
     * @method currentSizeIsLarger
     * @param minScreenSize {String} one of SCREEN_SIZES
     * @return {Boolean} true if the given screen size is smaller than the current screen size.  false otherwise.
     */
    currentSizeIsLarger: function(minScreenSize) {
      return this.compareToCurrentSize(minScreenSize) > 0;
    },

    /**
     * @method currentSizeIsSmaller
     * @param maxScreenSize {String} one of SCREEN_SIZES
     * @return {Boolean} true if the given screen size is larger than the current screen size.  false otherwise.
     */
    currentSizeIsSmaller: function(maxScreenSize) {
      return this.compareToCurrentSize(maxScreenSize) < 0;
    },

    /**
     * @method currentSizeIsEqual
     * @param equalScreenSize {String} one of SCREEN_SIZES
     * @return {Boolean} true if the given screen size is equal to the current screen size.  false otherwise.
     */
    currentSizeIsEqual: function(equalScreenSize) {
      return this.compareToCurrentSize(equalScreenSize) === 0;
    },

    /**
     * @method getScreenSize
     * @return {String} the screen size that will match one of the SCREEN_SIZE values.
     */
    getScreenSize: function() {
      return this.get('screenSize');
    },

    /**
     * Saves the screen size based on the visible dom.
     * @method _syncScreenSizeWithDom
     */
    _syncScreenSizeWithDom: function() {
      if (!this.isDisposed()) {
        var screenSizeValue = this.$el.find('[data-screen-size-value]:visible').attr('data-screen-size-value');
        this.set('screenSize', screenSizeValue);
      }
    },

    /**
     * @method initialize
     * @override
     */
    initialize: function() {
      _.bindAll(this, '_triggerScreenSizeChange', '_syncScreenSizeWithDom', '_throttledSyncScreenSizeWithDom');
      this.listenTo(this.viewState, 'change:screenSize', this._triggerScreenSizeChange);
      this.listenTo(this.viewState, 'change:screenSize', this.render);
    },

    /**
     * @method prerender
     * @override
     */
    prerender: function() {
      this._stopListeningToDomChanges();
    },

    /**
     * @method postrender
     * @override
     */
    postrender: function() {
      this._throttledSyncScreenSizeWithDom();
      this._triggerSyncOnDomChange();
      if (!this._domChangeObserver) {
        // Check again a little after postrender to increase reliability.
        // Other changes to the dom might cause different results from checking screen size.
        // This is only run if we can't update whenever the dom changes.
        // This is scaled up to give it plenty of time for dom to finish changing.
        window.setTimeout(this._throttledSyncScreenSizeWithDom, RESIZE_THROTTLE_MS_BETWEEN_CALLS * 5);
      }
    },

    /**
     * @method _activate
     * @override
     * @private
     */
    _activate: function() {
      this._throttledSyncScreenSizeWithDom();
      $(window).on('resize', this._throttledSyncScreenSizeWithDom);
      this._triggerSyncOnDomChange();
    },

    /**
     * @method _deactivate
     * @override
     * @private
     */
    _deactivate: function() {
      $(window).off('resize', this._throttledSyncScreenSizeWithDom);
      this._stopListeningToDomChanges();
    },

    /**
     * Retrigger the screen size change event with a payload of the screen size.
     * @method _triggerScreenSizeChange
     * @private
     */
    _triggerScreenSizeChange: function() {
      this.trigger('change:screenSize', this.getScreenSize());
    },

    /**
     * Throttled version of _syncScreenSizeWithDom()
     * @method _throttledSyncScreenSizeWithDom
     * @private
     */
    _throttledSyncScreenSizeWithDom: function() {
      this._throttledSyncScreenSizeWithDom = _.throttle(this._syncScreenSizeWithDom, RESIZE_THROTTLE_MS_BETWEEN_CALLS);
      this._throttledSyncScreenSizeWithDom();
    },

    /**
     * Try the new(ish) MutationObserver to determine if there are active dom changes and trigger when they complete.
     * @method _triggerSyncOnDomChange
     * @private
     */
    _triggerSyncOnDomChange: function() {
      this._stopListeningToDomChanges();
      var MutationObserver = window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;
      if (!MutationObserver) {
        // not supported :(
        return;
      }
      var bodyElement = $('body')[0];

      this._domChangeObserver = new MutationObserver(this._throttledSyncScreenSizeWithDom);

      this._domChangeObserver.observe(bodyElement, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    },

    /**
     * Stop listening for DOM changes.
     * @method _stopListeningToDomChanges
     * @private
     */
    _stopListeningToDomChanges: function() {
      if (this._domChangeObserver && _.isFunction(this._domChangeObserver.disconnect)) {
        this._domChangeObserver.disconnect();
        delete this._domChangeObserver;
      }
    }
  });

  return new ScreenSizeView();
}));
